import { Engine } from "./engine";
import * as FPSIndicator from "./fps-indicator";

import * as GLCanvas from "./gl-utils/gl-canvas";
import { gl } from "./gl-utils/gl-canvas";

import { Parameters } from "./parameters";
import { Visor } from "./visor";

// Page interface is implemented in HTML


function main(): void {
    const webglFlags = {
        alpha: false,
        antialias: false,
        depth: false,
        stencil: false,
        preserveDrawingBuffer: false,
    };
    if (!GLCanvas.initGL(webglFlags)) {
        return;
    }
    gl.disable(gl.CULL_FACE);
    gl.disable(gl.BLEND);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.STENCIL_TEST);

    const canvas = Page.Canvas.getCanvas();

    Parameters.blurChangeObservers.push(() => { if (canvas) updateBlur(canvas); });
    if (canvas) updateBlur(canvas);

    let needToAdjustCanvasSize = true;
    
    // Modern ResizeObserver for efficient, asynchronous canvas resizing
    if (canvas && window.ResizeObserver) {
        const resizeObserver = new ResizeObserver(() => {
            // Process resize immediately and asynchronously
            requestAnimationFrame(() => {
                GLCanvas.adjustSize(false);
                // console.log(`Canvas resized: ${canvas.width}x${canvas.height}, client: ${canvas.clientWidth}x${canvas.clientHeight}`);
                engine.resize(canvas.width, canvas.height);
            });
        });
        resizeObserver.observe(canvas);
        
        // Fallback for legacy resize events
        Parameters.canvasResizeObservers.push(() => { needToAdjustCanvasSize = true; });
    } else {
        // Fallback for browsers without ResizeObserver
        Parameters.canvasResizeObservers.push(() => { 
            needToAdjustCanvasSize = true;
            // Immediate handling for fallback
            if (canvas) {
                GLCanvas.adjustSize(false);
                engine.resize(canvas.width, canvas.height);
                needToAdjustCanvasSize = false;
            }
        });
    }

    let needToReset = true;
    Parameters.resetObservers.push(() => { needToReset = true; });

    let needToDownload = false;
    Parameters.imageDownloadObservers.push(() => { needToDownload = true; });

    const engine = new Engine();
    const visor = new Visor();

    let lastFrameTime = 0;
    const TARGET_FPS = 30; // Lower FPS when high frequency is disabled
    const frameInterval = 1000 / TARGET_FPS;

    function mainLoop(currentTime: number = 0): void {
        // Throttle frame rate when high frequency canvas updates is disabled
        if (!Parameters.highFreqCanvasEnabled) {
            if (currentTime - lastFrameTime < frameInterval) {
                requestAnimationFrame(mainLoop);
                return;
            }
            lastFrameTime = currentTime;
        }

        FPSIndicator.registerFrame();

        if (needToDownload) {
            // redraw before resizing the canvas because the download pane might open, which changes the canvas size
            engine.drawToCanvas(); // redraw because preserveDrawingBuffer is false
            if (canvas) download(canvas);
            needToDownload = false;
        }

        if (needToAdjustCanvasSize) {
            GLCanvas.adjustSize(false);
            if (canvas) engine.resize(canvas.width, canvas.height);
            needToAdjustCanvasSize = false;
        }

        if (needToReset) {
            engine.reset();
            needToReset = false;
        }

        engine.update();
        engine.drawToCanvas();

        visor.update();

        if (Parameters.displayBrush) {
            engine.displayBrush();
        }

        requestAnimationFrame(mainLoop);
    }
    mainLoop();
}

function updateBlur(canvas: HTMLElement): void {
    const blur = Parameters.blur;
    if (blur <= 0) {
        canvas.style.filter = "";
    } else {
        canvas.style.filter = `blur(${blur}px)`;
    }
}

function download(canvas: HTMLCanvasElement): void {
    const name = "reaction-diffusion.png";

    if ((canvas as any).msToBlob) { // for IE
        const blob = (canvas as any).msToBlob();
        (window.navigator as any).msSaveBlob(blob, name);
    } else {
        canvas.toBlob((blob: Blob | null) => {
            if (blob) {
                const link = document.createElement("a");
                link.download = name;
                link.href = URL.createObjectURL(blob);
                link.click();
            }
        });
    }
}

main();
